import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  type ReactNode,
} from "react";

import { getOffersWithStats } from "@/lib/offerStats";
import { useAppDispatch, useAppSelector } from "@/store";
import {
  fetchOfferRedemptionCounts,
  selectWorkableOffers,
} from "@/store/slices/offersSlice";
import { calculateSubtotal } from "@/utils/pricing";
import type { OfferWithStats } from "@/types/wallet";

/**
 * `OffersContext` — the original's offers provider (module 80603).
 *
 * It owns three things and the drawer owns none of them:
 *
 * 1. `offersWithStats` — every workable offer paired with what the customer's
 *    history and the current basket make of it.
 * 2. `totalClaimableOffers` — the ids behind the header's "Offers (n)" chip.
 *    Note the original FILTERS out anything already redeemed before publishing
 *    it, so the chip counts what can still be claimed, not what is eligible.
 * 3. The one HTTP call the wallet makes: the per-customer redemption counts,
 *    fired only when signed in, not staff, the auth check has settled and there
 *    is at least one offer.
 *
 * Staff zero everything out — a staff order has no wallet — which is the
 * original's own short-circuit, not a guard added here.
 */

interface OffersContextValue {
  offersWithStats: OfferWithStats[];
  totalClaimableOffers: string[];
}

/** Stable empties, so an unchanged context does not re-render its consumers. */
const EMPTY_OFFERS: OfferWithStats[] = [];
const EMPTY_IDS: string[] = [];

const OffersContext = createContext<OffersContextValue>({
  offersWithStats: EMPTY_OFFERS,
  totalClaimableOffers: EMPTY_IDS,
});

export const useOffersContext = () => useContext(OffersContext);

export function OffersProvider({ children }: { children: ReactNode }) {
  const dispatch = useAppDispatch();
  const workableOffers = useAppSelector(selectWorkableOffers);
  const customer = useAppSelector((state) => state.auth.customer);
  const isStaff = useAppSelector((state) => state.auth.isStaff);
  const isAuthCheckComplete = useAppSelector(
    (state) => state.common.isAuthCheckComplete,
  );
  const redemptionCountsFetched = useAppSelector(
    (state) => state.offers.redemptionCountsFetched,
  );
  const customerOfferRedemptionCounts = useAppSelector(
    (state) => state.offers.customerOfferRedemptionCounts,
  );
  const vouchers = useAppSelector((state) => state.vouchers.data);
  const giftCards = useAppSelector((state) => state.giftCards.data);
  const currencySymbol = useAppSelector(
    (state) => state.settings.data?.currency_symbol,
  );
  const orderType = useAppSelector((state) => state.cart.orderType);
  const cartItems = useAppSelector((state) => state.cart.items);
  const redeemedOffers = useAppSelector((state) => state.cart.redeemed_offers);
  const orderDiscountDetails = useAppSelector(
    (state) => state.cart.order_discount_details,
  );

  const isLoggedIn = useAppSelector((state) => state.auth.isLoggedIn);
  const itemsPrice = useMemo(() => calculateSubtotal(cartItems), [cartItems]);

  // The original's condition, verbatim: not staff, the auth check has settled,
  // signed in, and there is something to ask about.
  useEffect(() => {
    if (isStaff || !isAuthCheckComplete || !isLoggedIn) return;
    if (workableOffers.length === 0) return;
    void dispatch(fetchOfferRedemptionCounts());
  }, [dispatch, isStaff, isAuthCheckComplete, isLoggedIn, workableOffers.length]);

  const value = useMemo<OffersContextValue>(() => {
    if (isStaff || workableOffers.length === 0) {
      return {
        offersWithStats: EMPTY_OFFERS,
        totalClaimableOffers: EMPTY_IDS,
      };
    }
    const result = getOffersWithStats({
      offers: workableOffers,
      customer,
      vouchers,
      giftCards,
      customerOfferRedemptionCounts,
      redeemed_offers: redeemedOffers,
      platform: `WEB_${orderType}`,
      currency_symbol: currencySymbol,
      currentItemsPrice: itemsPrice,
      totalDiscountApplied: 0,
      order_discount_details: orderDiscountDetails,
    });
    const offersWithStats = result?.offersWithStats ?? EMPTY_OFFERS;
    // Signed in but the counts have not landed yet: the original publishes an
    // empty chip rather than a count it may have to revise downwards.
    if (isLoggedIn && !redemptionCountsFetched) {
      return { offersWithStats, totalClaimableOffers: EMPTY_IDS };
    }
    const claimable = (result?.totalClaimableOffers ?? EMPTY_IDS).filter((id) => {
      const entry = offersWithStats.find((row) => row.offer._id === id);
      return entry == null || entry.stats.redeemed_times <= 0;
    });
    return { offersWithStats, totalClaimableOffers: claimable };
  }, [
    isStaff,
    workableOffers,
    customer,
    vouchers,
    giftCards,
    customerOfferRedemptionCounts,
    redeemedOffers,
    orderType,
    currencySymbol,
    itemsPrice,
    orderDiscountDetails,
    isLoggedIn,
    redemptionCountsFetched,
  ]);

  return (
    <OffersContext.Provider value={value}>{children}</OffersContext.Provider>
  );
}
